import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, idrShort } from "../../lib/api";
import { toast } from "sonner";
import { Boxes, Plus, Barcode, Warehouse, TrendingDown, TrendingUp, Search, ClipboardCheck, X, Check, AlertTriangle, ChevronRight } from "lucide-react";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { hariIniISO, formatTanggal } from "../../lib/tanggal";
import { useAuth } from "../../context/AuthContext";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";
import { labelKategori, warnaKategori } from "../../lib/kategoriBarang";

// `/finance/persediaan/products` & `/finance/persediaan/stock` sama-sama
// memotong `to_list(1000)` di backend (lihat persediaan.py) — sort di tabel
// masing-masing murni client-side, didampingi `BatasDaftarNotice`.
const PERSEDIAAN_BATAS_SERVER = 1000;

// Tab "Pembelian Stok" DIHAPUS (poin 9 owner 19 Agu) — jalur ini memicu fraud
// (menggeser nilai persediaan antar akun neraca tanpa gudang/lapisan modal,
// HPP tidak bisa dihitung) dan endpoint-nya SUDAH ditutup sejak lama di backend
// (`POST /finance/persediaan/pembelian` selalu menolak 400, lihat
// `backend/routers/finance/persediaan.py`). Tombolnya bukan sekadar
// disembunyikan tapi memang mati — menghapusnya bukan mengurangi fitur.
// Pembelian stok SATU-SATUNYA jalur sah: Inventory › Pembelian & Terima
// (`/inventory/pembelian`), lihat tautan di header halaman ini.
const TABS = [
  { key: "products", label: "Master Barang",   icon: Barcode },
  { key: "stock",    label: "Stok & Nilai",     icon: Boxes },
  { key: "opname",   label: "Stock Opname",     icon: ClipboardCheck },
];
// Label & warna kategori dipindah ke lib/kategoriBarang.js (SATU sumber
// dipakai bareng Sales.jsx/InputNota.jsx) — dulu daftar statis 3 kategori
// lawas di sini ("unit_baru" dkk) selalu ketinggalan begitu backend menambah
// kategori baru (lihat komentar `labelKategori`/`warnaKategori` di bawah).

export default function FinancePersediaan() {
  const [tab, setTab] = useState("stock");
  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-persediaan">
      <div>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Persediaan · Inventory</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Barang Dagang & Stok</h1>
        <p className="text-sm text-slate-600 mt-1">HPP otomatis · Nilai persediaan real-time · Stock opname dengan approval SPV</p>
        {/* Dulu ada tab "Pembelian Stok" di sini — dihapus (poin 9), diganti
            petunjuk supaya staf yang terbiasa memakainya tahu ke mana pindah. */}
        <p className="text-xs text-slate-500 mt-1">
          Pencatatan pembelian stok baru dilakukan di{" "}
          <Link to="/inventory/pembelian" data-testid="persediaan-link-pembelian"
                className="text-emerald-700 hover:underline font-medium">Inventory › Pembelian &amp; Terima</Link>{" "}
          — supaya gudang, lapisan modal, dan HPP ikut tervalidasi.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex gap-1 overflow-x-auto max-w-full">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`persediaan-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                tab === t.key ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "products" && <TabProducts />}
      {tab === "stock" && <TabStock />}
      {tab === "opname" && <TabOpname />}
    </div>
  );
}

function TabProducts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = () => {
    setLoading(true);
    setLoadError(null);
    api.get("/finance/persediaan/products")
      .then(r => setItems(r.data.items || []))
      .catch((e) => { setLoadError(e); toast.error("Gagal load"); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  const filtered = items.filter(p => !q || p.nama.toLowerCase().includes(q.toLowerCase()) || p.sku.toLowerCase().includes(q.toLowerCase()));

  // Urutan bawaan (nama asc dari backend) tetap sampai header diklik. Batas
  // dicek dari `items` (hasil fetch mentah), bukan `filtered` (pencarian
  // client tentu selalu <= itu) — supaya penanda tidak muncul palsu saat
  // pengguna sekadar mengetik di kotak cari.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedFiltered = sortRows(filtered, sortKey, sortDir, {
    sku: (p) => p.sku || "",
    nama: (p) => p.nama || "",
    kategori: (p) => p.kategori || "",
    brand: (p) => p.brand || "",
    harga_beli: (p) => p.harga_beli || 0,
    harga_jual: (p) => p.harga_jual || 0,
    stok: (p) => p.stock_qty || 0,
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari SKU/nama..." className="w-full h-10 pl-10 pr-3 border border-slate-200 rounded-lg text-sm" />
        </div>
        {/* Master barang PINDAH ke Inventory. Jalur lama ditutup di backend karena
            terbuka untuk semua user dan bisa menggeser nilai persediaan antar akun
            neraca tanpa jurnal, sekaligus melewati pagar costing/IMEI/barcode.
            Tombolnya diarahkan ke sana, bukan dibiarkan menabrak error 400. */}
        <Link to="/inventory/produk" data-testid="prod-new"
              className="h-10 px-4 rounded-lg bg-teal-600 text-white hover:bg-teal-700 text-sm font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Barang Baru
        </Link>
      </div>
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
        Halaman ini kacamata <b>akuntansi</b> persediaan (nilai & mutasi). Menambah atau
        mengubah master barang dilakukan di <b>Inventory › Master Produk</b> supaya metode
        costing, IMEI/SN, dan barcode ikut tervalidasi.
      </div>
      <BatasDaftarNotice shown={items.length} batas={PERSEDIAAN_BATAS_SERVER} />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label="SKU" sortKeyName="sku" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="prod-sort-sku" />
                <SortTh label="Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="prod-sort-nama" />
                <SortTh label="Kategori" sortKeyName="kategori" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="prod-sort-kategori" />
                <SortTh label="Brand" sortKeyName="brand" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="prod-sort-brand" />
                <SortTh label="Harga Beli" sortKeyName="harga_beli" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="prod-sort-hargabeli" />
                <SortTh label="Harga Jual" sortKeyName="harga_jual" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="prod-sort-hargajual" />
                <SortTh label="Stok" sortKeyName="stok" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="prod-sort-stok" />
                <th className="px-4 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              <LoadingState loading={loading} error={loadError} onRetry={load} kosong={filtered.length === 0} pesanKosong={q ? "Tidak ada hasil" : "Belum ada master barang"} varian="tabel" kolom={8} data-testid="loading-persediaan-products">
              {sortedFiltered.map(p => {
                  const warna = warnaKategori(p.kategori);
                  return (
                    <tr key={p.sku} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-2 font-mono text-xs">{p.sku}</td>
                      <td className="px-4 py-2 font-medium">{p.nama}</td>
                      <td className="px-4 py-2"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-${warna}-50 text-${warna}-700 border-${warna}-200`}>{labelKategori(p.kategori)}</span></td>
                      <td className="px-4 py-2 text-slate-600 text-xs">{p.brand || "-"}</td>
                      <td className="px-4 py-2 text-right font-mono">{idrShort(p.harga_beli)}</td>
                      <td className="px-4 py-2 text-right font-mono">{idrShort(p.harga_jual)}</td>
                      <td className="px-4 py-2 text-right font-semibold">{p.stock_qty} {p.min_stock && p.stock_qty <= p.min_stock ? <AlertTriangle className="w-3 h-3 inline text-rose-500" /> : null}</td>
                      {/* Menyunting master barang pindah ke Inventory — jalur lama
                          ditutup backend karena bisa menggeser nilai persediaan antar
                          akun neraca tanpa jurnal. Tautan bawa SKU-nya supaya kasir
                          tidak perlu mencari ulang. */}
                      <td className="px-4 py-2 text-right">
                        <Link to={`/inventory/produk?sku=${encodeURIComponent(p.sku)}`}
                              className="text-xs text-emerald-700 hover:underline">Ubah di Inventory</Link>
                      </td>
                    </tr>
                  );
                })}
              </LoadingState>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children, col2 }){ return <div className={col2 ? "col-span-2" : ""}><label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>{children}</div>; }

// Label & warna kartu kategori di tab "Stok & Nilai" — dari lib/kategoriBarang.js
// (satu sumber, lihat impor di atas berkas). Dulu tab ini memetakan langsung ke
// KATEGORI (const statis 3 kategori lawas) lalu `.slice(0, 2)` di ATAS hasil .map() —
// sehingga kartu "Aksesoris" selalu dibuang diam-diam, dan kategori baru dari backend
// (mis. "sim_sticker", lihat backend/routers/finance/persediaan.py yang sekarang
// mengikuti KATEGORI dinamis dari routers/inventory/_shared.py) tidak pernah terlihat
// sama sekali walau datanya sudah dikirim. Sekarang kartu dirender dinamis dari kunci
// yang BENAR-BENAR ada di data.per_kategori, bukan daftar statis di frontend, dan
// `labelKategori`/`warnaKategori` mengenali kunci kanonik BARU maupun alias lama.

function TabStock() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = () => {
    setLoading(true);
    setLoadError(null);
    api.get("/finance/persediaan/stock")
      .then(r => setData(r.data))
      .catch((e) => { setLoadError(e); toast.error("Gagal load"); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  // Guard: per_kategori/low_stock/items dijaga dengan fallback array/objek kosong supaya
  // tab tidak pernah crash/blank walau salah satu field kosong dari backend.
  const perKategori = data?.per_kategori || {};
  const kategoriKeys = Object.keys(perKategori);
  const lowStock = data?.low_stock || [];
  const items = data?.items || [];

  // Urutan bawaan (nilai desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    nama: (p) => p.nama || "",
    qty: (p) => p.qty || 0,
    harga_avg: (p) => p.harga_avg || 0,
    nilai: (p) => p.nilai || 0,
  });

  return (
    <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={4} data-testid="loading-persediaan-stock">
    {data && <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Qty Stok</p>
          <p className="font-display text-2xl font-bold text-slate-900 mt-1">{data.total_qty ?? 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Nilai Persediaan</p>
          <p className="font-display text-2xl font-bold text-teal-700 mt-1">{idrShort(data.total_nilai || 0)}</p>
        </div>
        {kategoriKeys.length === 0 ? (
          <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-xl p-4 grid place-items-center text-xs text-slate-500">
            Belum ada kategori barang
          </div>
        ) : kategoriKeys.map(k => {
          const c = warnaKategori(k);
          return (
            <div key={k} className={`bg-${c}-50 border border-${c}-200 rounded-xl p-4`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wider text-${c}-700`}>{labelKategori(k)}</p>
              <p className={`font-display text-lg font-bold text-${c}-800 mt-1`}>{perKategori[k]?.qty || 0} pcs</p>
              <p className={`text-xs text-${c}-700`}>{idrShort(perKategori[k]?.nilai || 0)}</p>
            </div>
          );
        })}
      </div>
      {lowStock.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-700 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Stok Rendah</p>
          <div className="space-y-1">{lowStock.map(l => <p key={l.sku} className="text-sm text-rose-800"><b>{l.nama}</b> · qty {l.qty} (min {l.min})</p>)}</div>
        </div>
      )}
      <BatasDaftarNotice shown={items.length} batas={PERSEDIAAN_BATAS_SERVER} />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-[11px] uppercase text-slate-500"><tr>
            <SortTh label="SKU / Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="stock-sort-nama" />
            <SortTh label="Qty" sortKeyName="qty" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="stock-sort-qty" />
            <SortTh label="Harga Avg" sortKeyName="harga_avg" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="stock-sort-hargaavg" />
            <SortTh label="Nilai Total" sortKeyName="nilai" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="stock-sort-nilai" />
          </tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={4} className="p-10 text-center text-slate-400">Belum ada stok</td></tr> :
              sortedItems.map(p => (
                <tr key={p.sku} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-2"><p className="font-medium">{p.nama}</p><p className="text-[10px] font-mono text-slate-400">{p.sku}</p></td>
                  <td className="px-4 py-2 text-right font-semibold">{p.qty}</td>
                  <td className="px-4 py-2 text-right font-mono text-slate-600">{idrShort(p.harga_avg)}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-teal-700">{idrShort(p.nilai)}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>}
    </LoadingState>
  );
}

function TabOpname() {
  const { can, user } = useAuth();
  // Kunci `inventory_opname`, BUKAN `finance_persediaan` — satu konsep opname
  // (sama dengan Blind Opname Inventory) harus punya satu kunci izin, persis
  // gerbang yang ditegakkan backend di `routers/finance/persediaan.py`. Kalau
  // kunci di sini beda dari backend, tombol bisa tampil tapi berakhir 403,
  // atau sebaliknya tersembunyi padahal endpoint sebenarnya mengizinkan.
  const canCreate = can("inventory_opname", "create");

  // Approve/reject SENGAJA lebih sempit daripada `can(...)` biasa, meniru
  // `_boleh_putuskan_opname` di backend. Alasannya: `defaultCan` (dan
  // `authorize_module` di server) memberi `finance_staff` aksi update pada
  // SEMUA modul inventory_* sebagai bawaan role. Kalau tombol Setujui memakai
  // `can("inventory_opname","update")` polos, ia akan MUNCUL untuk staf
  // Finance lalu berakhir 403 — persis yang dilarang aturan #3 CLAUDE.md.
  // Dan memang seharusnya 403: approve-lah yang menjurnal nilai persediaan,
  // dan alur ini dirancang "staf mengajukan → SPV memutuskan".
  // Hanya baris izin `inventory_opname` yang dicentang eksplisit yang dihitung —
  // saklar induk `inventory` SENGAJA tidak, supaya persis sama dengan
  // `izin_eksplisit_finance` di backend (yang hanya mengenal saklar induk
  // `finance`). Menerima saklar induk di sini akan menampilkan tombol yang
  // ujungnya tetap ditolak server.
  const ROLE_PEMUTUS = ["superadmin", "admin", "finance_spv", "finance_ceo"];
  const canApprove =
    ROLE_PEMUTUS.includes(user?.role)
    || user?.permissions?.inventory_opname?.update === true;
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = () => {
    setLoading(true);
    setLoadError(null);
    api.get("/finance/persediaan/opname")
      .then(r => setItems(r.data.items || []))
      .catch((e) => { setLoadError(e); toast.error("Gagal memuat opname"); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/finance/persediaan/products").then(r => setProducts(r.data.items || [])); }, []);
  const decide = async (id, action) => {
    let body = {}; if (action === "reject") { const reason = prompt("Alasan tolak?"); if (!reason) return; body = { reason }; }
    try { const r = await api.post(`/finance/persediaan/opname/${id}/${action}`, body); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="space-y-4">
      {canCreate ? (
        <button onClick={() => setShowForm(true)} data-testid="opn-new" className="h-10 px-4 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Opname Baru</button>
      ) : (
        // Jangan tampilkan tombol yang ujungnya 403 (aturan #3 CLAUDE.md) — jelaskan
        // kenapa, bukan diam-diam menghilangkannya seolah memang tidak ada fitur ini.
        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="opn-tanpa-izin-create">
          Izinmu belum mencakup mengajukan opname. Minta admin mencentang &ldquo;Inventory · Stok Opname&rdquo;
          bagian &ldquo;create&rdquo; di User Management kalau kamu perlu mengajukan.
        </p>
      )}
      <div className="space-y-2">
        <LoadingState loading={loading} error={loadError} onRetry={load} kosong={items.length === 0} pesanKosong="Belum ada opname" varian="kartu" jumlahKartu={2} data-testid="loading-opname">
        {items.map(o => (
            <div key={o.id} className={`border rounded-xl p-4 ${o.status === "pending" ? "bg-amber-50 border-amber-200" : o.status === "approved" ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-mono text-slate-500">{o.code} · {formatTanggal(o.tanggal)}</p>
                  <p className="font-medium text-slate-900">{o.nama_produk}</p>
                  <p className="text-sm text-slate-700 mt-1">Sistem: <b>{o.qty_sistem}</b> · Fisik: <b>{o.qty_fisik}</b> · Selisih: <b className={o.selisih < 0 ? "text-rose-700" : "text-emerald-700"}>{o.selisih > 0 ? "+" : ""}{o.selisih}</b> ({idrShort(o.nilai_selisih)})</p>
                  {o.catatan && <p className="text-xs text-slate-600 mt-1 italic">&quot;{o.catatan}&quot;</p>}
                </div>
                {o.status === "pending" && (
                  canApprove ? (
                    <div className="flex gap-2">
                      <button onClick={() => decide(o.id, "reject")} className="h-9 px-3 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-medium border border-rose-200"><X className="w-3.5 h-3.5" /></button>
                      <button onClick={() => decide(o.id, "approve")} data-testid={`opn-appr-${o.id}`} className="h-9 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-medium inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Setujui</button>
                    </div>
                  ) : (
                    // Sebelumnya SPV/CEO/admin/superadmin selalu bisa (allowlist role keras) —
                    // pemegang izin yang dicentang bos sekarang IKUT bisa, tapi yang tidak
                    // keduanya harus melihat alasan, bukan tombol yang diam-diam hilang.
                    <span className="text-[11px] text-slate-500 italic" data-testid={`opn-tanpa-izin-approve-${o.id}`}>
                      Menunggu SPV/pemegang izin approve
                    </span>
                  )
                )}
                {o.status !== "pending" && <span className={`text-[10px] font-semibold uppercase ${o.status === "approved" ? "text-emerald-700" : "text-slate-500"}`}>{o.status}</span>}
              </div>
            </div>
        ))}
        </LoadingState>
      </div>
      {showForm && <FormOpname products={products} onClose={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
function FormOpname({ products, onClose }) {
  const [f, setF] = useState({ tanggal: hariIniISO(), sku: "", warehouse_id: "", qty_fisik: "", brand: "", cabang: "", catatan: "" });
  const [busy, setBusy] = useState(false);
  // Gudang WAJIB: penyesuaian opname menggerakkan lapisan modal dan kartu stok
  // PER GUDANG. Tanpa gudang, penyesuaian tidak terlihat oleh gerbang transfer
  // maupun gerbang nonaktifkan gudang — barang yang sudah dinyatakan hilang
  // masih bisa dipindahkan.
  const [gudang, setGudang] = useState([]);
  useEffect(() => {
    let batal = false;
    api.get("/inventory/warehouses")
      .then(r => { if (!batal) setGudang(r.data.items || []); })
      .catch(() => {});
    return () => { batal = true; };
  }, []);
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post("/finance/persediaan/opname", { ...f, qty_fisik: Number(f.qty_fisik) }); toast.success(r.data.message); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-4">Stock Opname</h3>
        <div className="space-y-3">
          <Fld label="Tanggal"><DatePicker value={f.tanggal} onChange={v => setF({ ...f, tanggal: v })} data-testid="opn-tanggal" /></Fld>
          <Fld label="Barang"><select value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} className="in" data-testid="opn-sku"><option value="">Pilih SKU...</option>{products.map(p => <option key={p.sku} value={p.sku}>{p.nama} (stok sistem: {p.stock_qty})</option>)}</select></Fld>
          <Fld label="Gudang yang dihitung">
            <select value={f.warehouse_id} onChange={e => setF({ ...f, warehouse_id: e.target.value })}
                    className="in" data-testid="opn-gudang">
              <option value="">Pilih gudang...</option>
              {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
            </select>
            {gudang.length === 0 && (
              <p className="text-[11px] text-amber-700 mt-1">
                Belum ada gudang. Buat dulu di Inventory › Stok &amp; Gudang — opname dihitung per gudang.
              </p>
            )}
          </Fld>
          <Fld label="Qty Fisik (hitungan tangan)"><input type="number" min="0" value={f.qty_fisik} onChange={e => setF({ ...f, qty_fisik: e.target.value })} className="in" data-testid="opn-qty" /></Fld>
          <Fld label="Catatan"><textarea rows={2} value={f.catatan} onChange={e => setF({ ...f, catatan: e.target.value })} className="in" style={{ height: "auto", padding: "10px 12px" }} /></Fld>
        </div>
        <div className="flex gap-2 mt-5"><button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button><button onClick={submit} disabled={busy || !f.sku || !f.warehouse_id || f.qty_fisik === "" || Number(f.qty_fisik) < 0} data-testid="opn-submit" className="flex-1 h-10 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Ajukan Opname"}</button></div>
        <p className="text-[11px] text-slate-500 mt-2">💡 Opname masuk status <b>pending</b> dulu — butuh approval SPV sebelum stok disesuaikan.</p>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #0F172A; }`}</style>
      </div>
    </div>
  );
}
